import React, { useState } from 'react';
import SEO from '../components/SEO';
import { generateLocalBusinessSchema } from '../lib/structuredData';
import { FaFacebook, FaInstagram, FaTwitter, FaPinterest, FaYoutube, FaTiktok } from 'react-icons/fa';

export default function About() {
  const localBusinessSchema = generateLocalBusinessSchema();
  const [isAnimating, setIsAnimating] = useState(false);

  // Function to trigger meteoroid animation
  const triggerMeteoroidAnimation = () => {
    setIsAnimating(true);
    // Create particles at random positions
    for (let i = 0; i < 20; i++) {
      const particle = document.createElement('div');
      particle.className = 'meteoroid-particle';
      particle.style.cssText = `
        position: fixed;
        width: 4px;
        height: 4px;
        background: linear-gradient(45deg, #ff69b4, #ff1493);
        border-radius: 50%;
        pointer-events: none;
        z-index: 9999;
        left: ${Math.random() * window.innerWidth}px;
        top: ${Math.random() * window.innerHeight}px;
        animation: meteoroidFall 2s ease-out forwards;
        box-shadow: 0 0 10px #ff69b4;
      `;
      document.body.appendChild(particle);
      
      setTimeout(() => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }, 2000);
    }
    
    setTimeout(() => setIsAnimating(false), 2000);
  };

  // Add CSS animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes meteoroidFall {
        0% {
          transform: translateY(-100px) scale(1);
          opacity: 1;
        }
        50% {
          transform: translateY(50vh) scale(1.2);
          opacity: 0.8;
        }
        100% {
          transform: translateY(100vh) scale(0.5);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  return (
    <>
      <SEO
        title="About Us | BaileBelle - Premium Ballerina Dresses & Jewelry"
        description="Learn about BaileBelle's commitment to providing premium ballerina dresses and jewelry. Discover our story, values, and dedication to dance excellence."
        keywords="about BaileBelle, ballerina dress company, dance wear brand, ballet jewelry, company story, dance wear manufacturer"
        url="/about"
        structuredData={localBusinessSchema}
      />
      <div className="min-h-screen bg-black text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
            About BaileBelle
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            We are passionate about creating beautiful, high-quality dance wear and jewelry for dancers of all levels.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              BaileBelle was founded with a simple mission: to provide dancers with the most beautiful, 
              comfortable, and high-quality dance wear and accessories. We understand that every dancer 
              deserves to feel confident and elegant, whether they're taking their first ballet class 
              or performing on the world's biggest stages.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
              Our team of designers and craftspeople work tirelessly to create pieces that combine 
              traditional elegance with modern comfort and style. Every item in our collection is 
              carefully selected and tested to ensure it meets our high standards for quality and beauty.
            </p>
          </div>
          <div className="relative">
            <img
              src="https://cdnbailebelle.pages.dev/about-us/1.jpeg"
              alt="About BaileBelle"
              className="w-full h-auto object-contain rounded-2xl"
            />
          </div>
        </div>

        {/* Additional Images Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="relative">
            <img
              src="https://cdnbailebelle.pages.dev/about-us/2.jpeg"
              alt="BaileBelle Collection"
              className="w-full h-auto object-contain rounded-2xl"
            />
          </div>
          <div className="relative">
            <img
              src="https://cdnbailebelle.pages.dev/about-us/3.jpeg"
              alt="BaileBelle Quality"
              className="w-full h-auto object-contain rounded-2xl"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-white">10+</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Years of Experience</h3>
            <p className="text-gray-400">Creating beautiful dance wear for dancers worldwide</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-white">1000+</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Happy Customers</h3>
            <p className="text-gray-400">Dancers who trust us with their performance needs</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-white">500+</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Products</h3>
            <p className="text-gray-400">Beautiful dresses, jewelry, and accessories</p>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Follow Us</h2>
          <div className="flex justify-center items-center space-x-6 mb-8">
            <a 
              href="https://facebook.com/bailebelle" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-pink-500 transition-colors transform hover:scale-110"
            >
              <FaFacebook size={32} />
            </a>
            <a 
              href="https://instagram.com/bailebelle" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-pink-500 transition-colors transform hover:scale-110"
            >
              <FaInstagram size={32} />
            </a>
            <a 
              href="https://twitter.com/bailebelle" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-pink-500 transition-colors transform hover:scale-110"
            >
              <FaTwitter size={32} />
            </a>
            <a 
              href="https://pinterest.com/bailebelle" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-pink-500 transition-colors transform hover:scale-110"
            >
              <FaPinterest size={32} />
            </a>
            <a 
              href="https://youtube.com/bailebelle" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-pink-500 transition-colors transform hover:scale-110"
            >
              <FaYoutube size={32} />
            </a>
            <a 
              href="https://tiktok.com/@bailebelle" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-pink-500 transition-colors transform hover:scale-110"
            >
              <FaTiktok size={32} />
            </a>
          </div>
        </div>

        {/* Our Stores Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Our Stores</h2>
          <div className="flex justify-center items-center space-x-12">
            <div 
              className="cursor-pointer transform hover:scale-110 transition-all duration-300"
              onClick={triggerMeteoroidAnimation}
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-shadow">
                <div className="text-4xl font-bold text-black mb-2">Etsy</div>
                <div className="text-gray-600 text-sm">Visit our Etsy store</div>
              </div>
            </div>
            <div 
              className="cursor-pointer transform hover:scale-110 transition-all duration-300"
              onClick={triggerMeteoroidAnimation}
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-shadow">
                <div className="text-4xl font-bold text-black mb-2">eBay</div>
                <div className="text-gray-600 text-sm">Visit our eBay store</div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
          <p className="text-gray-400 text-lg leading-relaxed max-w-4xl mx-auto">
            To empower every dancer to express their passion and creativity through beautiful, 
            high-quality dance wear and accessories. We believe that when you look and feel 
            your best, you can dance your best.
          </p>
        </div>
      </div>
      </div>
    </>
  );
}
